import { useLocation, useNavigate } from 'react-router-dom';
import { useAppDispatch } from 'Douleutaras/redux/hooks';
import { useAB } from 'Douleutaras/app/lib/ab';
import { POST_JOB_FLOW } from 'Douleutaras/views/job/post-job/types';
import { getCategoryForm } from 'Douleutaras/redux/data/v2/categoryForm/categoryFormSlice';
import { storeCatalogueJobForm } from 'Douleutaras/views/job/post-job/helpers/catalogue-job-form';
import { resolveCatalogueSkip } from 'Douleutaras/views/job/post-job/helpers/catalogue-skip';
import {
  buildServicesSectionClickedProperties
} from 'Douleutaras/views/pro-profile/components/ServicesCatalogue/servicesSectionTracking';
import { ServicesSectionItem } from 'Douleutaras/app/lib/posthog/posthog.types';
import {
  CatalogueCategory,
  CatalogueService
} from 'Douleutaras/views/pro-profile/components/ServicesCatalogue/types';

export interface UseServicesCatalogueHandlersParams {
  flow?: POST_JOB_FLOW;
  autoDealId?: string;
}

export interface UseServicesCatalogueHandlersResult {
  handleServiceClick(service: CatalogueService): Promise<void>;
  handleCategoryExpand(category: CatalogueCategory): void;
  handleCategoryInfoClick(category: CatalogueCategory): void;
  handleServiceInfoClick(service: CatalogueService): void;
}

/**
 * The catalogue's booking and tracking callbacks: each row reports a PostHog `services section`
 * event, and a bookable row opens the same post-job form as the CTA with the steps it answers
 * pre-filled.
 */
export const useServicesCatalogueHandlers = (
  { flow, autoDealId }: UseServicesCatalogueHandlersParams
): UseServicesCatalogueHandlersResult => {
  const dispatch = useAppDispatch();
  const ab = useAB();
  const location = useLocation();
  const navigate = useNavigate();

  const navigateToPostJob = (categoryId?: string) => {
    const search = new URLSearchParams(location.search);

    // Names the category the form opens with, so its selection step is skipped and the answers
    // restored are the ones stored for that category.
    if (categoryId) {
      search.set('category', categoryId);
    }

    navigate(
      { pathname: 'post-job', search: search.toString() },
      { state: { flow, autoDealId }, replace: true }
    );
  };

  /**
   * A services-catalogue row opens the same booking form as the CTA, with the steps the row
   * already answers pre-filled.
   *
   * The row carries the form's question/answer ids. However, they only become form state against the
   * category's question set — that is what says which questions those ids are, and what lets a
   * stale id be dropped instead of applied blindly. So the category form is loaded first (it is
   * cached, and the booking form needs it next anyway), the ids are resolved against it, and the
   * result is written to the `job-form` storage the form already restores from. Whatever does
   * not resolve is simply not written: the form then opens with the category skipped and asks
   * the remaining questions as usual.
   */
  const handleServiceClick = async (service: CatalogueService) => {
    // @AB PostHog event
    ab.captureServicesSectionClicked(buildServicesSectionClickedProperties({
      item: service.level === 'sub1' ? ServicesSectionItem.SERVICE : ServicesSectionItem.DETAIL,
      tracking: service.tracking,
      price: service.price
    }));

    const booking = service.booking;

    if (!booking) {
      navigateToPostJob();

      return;
    }

    try {
      const categoryForm = await dispatch(getCategoryForm({ categoryId: booking.categoryId, seller: false }))
        .unwrap();

      storeCatalogueJobForm(booking.categoryId, resolveCatalogueSkip(categoryForm?.result, booking.pairs));
    } catch {
      // Without the question set, nothing can be pre-answered — open with the category skipped.
    }

    navigateToPostJob(booking.categoryId);
  };

  const handleCategoryExpand = (category: CatalogueCategory) => {
    // @AB PostHog event
    ab.captureServicesSectionClicked(buildServicesSectionClickedProperties({
      item: ServicesSectionItem.CATEGORY_EXPAND,
      tracking: category.tracking
    }));
  };

  const handleCategoryInfoClick = (category: CatalogueCategory) => {
    // @AB PostHog event
    ab.captureServicesSectionClicked(buildServicesSectionClickedProperties({
      item: ServicesSectionItem.CATEGORY_INFO,
      tracking: category.tracking
    }));
  };

  // The ⓘ opens the info modal instead of the booking flow, so it reports its own item
  const handleServiceInfoClick = (service: CatalogueService) => {
    // @AB PostHog event
    ab.captureServicesSectionClicked(buildServicesSectionClickedProperties({
      item: service.level === 'sub1' ? ServicesSectionItem.SERVICE_INFO : ServicesSectionItem.DETAIL_INFO,
      tracking: service.tracking,
      price: service.price
    }));
  };

  return { handleServiceClick, handleCategoryExpand, handleCategoryInfoClick, handleServiceInfoClick };
};
